<script lang="ts" setup>
import { computed } from "vue";
import cronstrue from "cronstrue";
import type { Schedule } from "@/types/schedule-types";

interface Props {
  schedule: Schedule;
  selected: boolean;
  /** Display name for the workflow this schedule runs (resolved by the parent). */
  workflowName?: string;
  /** Display name for the schedule's project. Shown only when provided (e.g. the global list,
   *  not the per-project tab where the project is implied). */
  projectName?: string;
  /** Whether the selection checkbox is shown (off in contexts without bulk actions, e.g. project tab). */
  selectable?: boolean;
}

const props = withDefaults(defineProps<Props>(), { selectable: true });
const emit = defineEmits<{
  "toggle-selection": [scheduleId: string];
  action: [action: string, scheduleId: string];
  edit: [scheduleId: string];
}>();

const isPaused = computed(() => props.schedule.status === "Paused");
const isActive = computed(() => props.schedule.status === "Active");

const statusConfig = computed(() => {
  switch (props.schedule.status) {
    case "Paused":
      return { badgeClass: "bg-warning", label: "Paused", icon: "bi-pause-circle" };
    case "Inactive":
      return { badgeClass: "bg-tertiary", label: "Inactive", icon: "bi-stop-circle" };
    case "Completed":
      return { badgeClass: "bg-tertiary", label: "Completed", icon: "bi-check2-all" };
    case "Expired":
      return { badgeClass: "bg-tertiary", label: "Expired", icon: "bi-hourglass-bottom" };
    default:
      // Active schedule. Surface a Failed last-run (actionable); otherwise show Active. A run's
      // transient "Running" state is reconciled back to Succeeded/Failed on completion, so it's
      // shown in the per-run history rather than as the schedule's headline badge.
      if (props.schedule.lastRunStatus === "Failed")
        return { badgeClass: "bg-danger", label: "Failed", icon: "bi-exclamation-circle" };
      return { badgeClass: "bg-success", label: "Active", icon: "bi-check-circle" };
  }
});

/** Human-readable recurrence — cron description for recurring, the run time for one-offs. */
const frequency = computed(() => {
  if (props.schedule.kind === "OneOff") {
    return props.schedule.oneOffRunAtUtc
      ? `Once on ${new Date(props.schedule.oneOffRunAtUtc).toLocaleString()}`
      : "Once";
  }
  try {
    return cronstrue.toString(props.schedule.cronExpression);
  } catch {
    return props.schedule.cronExpression || "Custom schedule";
  }
});

// The grey subtitle tag shows which workflow this schedule runs. Falls back gracefully while the
// workflow-name lookup is still loading or if the workflow can't be resolved.
const workflowLabel = computed(() => props.workflowName || "Unknown workflow");

const nextRunDisplay = computed(() => {
  if (!props.schedule.nextRunAtUtc) return "Not scheduled";

  const nextRun = new Date(props.schedule.nextRunAtUtc);
  const now = new Date();
  const diffMs = nextRun.getTime() - now.getTime();

  if (diffMs < 0) return "Overdue";
  if (diffMs < 60 * 60 * 1000)
    return `In ${Math.ceil(diffMs / (60 * 1000))} minutes`;
  if (diffMs < 24 * 60 * 60 * 1000)
    return `In ${Math.ceil(diffMs / (60 * 60 * 1000))} hours`;

  return nextRun.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
});

const lastRunDisplay = computed(() => {
  if (!props.schedule.lastRunAtUtc || props.schedule.lastRunStatus === "NeverRun") {
    return "Never run";
  }

  const lastRun = new Date(props.schedule.lastRunAtUtc);
  const now = new Date();
  const diffMs = now.getTime() - lastRun.getTime();

  if (diffMs < 60 * 1000) return "Just now";
  if (diffMs < 60 * 60 * 1000)
    return `${Math.floor(diffMs / (60 * 1000))} minutes ago`;
  if (diffMs < 24 * 60 * 60 * 1000)
    return `${Math.floor(diffMs / (60 * 60 * 1000))} hours ago`;
  if (diffMs < 7 * 24 * 60 * 60 * 1000)
    return `${Math.floor(diffMs / (24 * 60 * 60 * 1000))} days ago`;

  return lastRun.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
});

function toggleSelection() {
  emit("toggle-selection", props.schedule.id);
}

function handleAction(action: string) {
  emit("action", action, props.schedule.id);
}

function handleCardClick() {
  emit("edit", props.schedule.id);
}
</script>

<template>
  <div class="schedule-card" :class="{ selected: selected }">
    <div class="d-flex flex-column flex-md-row align-items-start gap-3 p-3">
      <!-- Selection Checkbox — its own left column on desktop; on mobile it sits inline
           beside the title (see .schedule-title-row) to avoid eating a full stacked row. -->
      <div v-if="selectable" class="selection-area flex-shrink-0 d-none d-md-block">
        <input
          type="checkbox"
          class="form-check-input"
          :checked="selected"
          @change="toggleSelection"
          @click.stop
        />
      </div>

      <!-- Main Content -->
      <div class="schedule-main flex-grow-1" @click="handleCardClick">
        <div class="schedule-header d-flex flex-column flex-md-row justify-content-between align-items-start mb-3 gap-2">
          <div class="schedule-title schedule-title-row d-flex align-items-start gap-2">
            <input
              v-if="selectable"
              type="checkbox"
              class="form-check-input flex-shrink-0 mt-1 d-md-none"
              :checked="selected"
              @change="toggleSelection"
              @click.stop
            />
            <div class="min-w-0">
              <h3 class="mb-1 h4">{{ schedule.name }}</h3>
              <div class="d-flex align-items-center gap-2 small flex-wrap">
                <span v-if="projectName" class="product-tag">
                  <i class="bi bi-folder me-1"></i>{{ projectName }}
                </span>
                <span class="product-tag">
                  <i class="bi bi-diagram-3 me-1"></i>{{ workflowLabel }}
                </span>
              </div>
            </div>
          </div>
          <div class="schedule-status flex-shrink-0">
            <span class="badge d-inline-flex align-items-center gap-1" :class="statusConfig.badgeClass">
              <i :class="statusConfig.icon"></i>
              {{ statusConfig.label }}
            </span>
          </div>
        </div>

        <div class="schedule-body">
          <p class="text-muted small mb-3">{{ schedule.description }}</p>

          <div class="schedule-timing">
            <div class="timing-item">
              <div class="timing-label text-muted text-uppercase fw-medium mb-1">Frequency</div>
              <div class="small fw-medium">{{ frequency }}</div>
            </div>
            <div class="timing-item">
              <div class="timing-label text-muted text-uppercase fw-medium mb-1">Next Run</div>
              <div
                class="small fw-medium"
                :class="{ 'text-danger': nextRunDisplay === 'Overdue' }"
              >
                {{ nextRunDisplay }}
              </div>
            </div>
            <div class="timing-item">
              <div class="timing-label text-muted text-uppercase fw-medium mb-1">Last Run</div>
              <div class="small fw-medium d-flex align-items-center gap-2">
                {{ lastRunDisplay }}
                <span
                  v-if="schedule.lastRunStatus !== 'NeverRun'"
                  class="run-status"
                  :class="schedule.lastRunStatus.toLowerCase()"
                >
                  <!-- Running = "fired, outcome pending": static hourglass, not a spinner, because
                       run completion isn't reported back yet (engine reconciliation is a fast-follow). -->
                  <i
                    :class="{
                      'bi-check-circle-fill':
                        schedule.lastRunStatus === 'Succeeded',
                      'bi-x-circle-fill': schedule.lastRunStatus === 'Failed',
                      'bi-hourglass-split':
                        schedule.lastRunStatus === 'Running',
                    }"
                  ></i>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="schedule-actions flex-shrink-0 d-flex align-items-start align-self-stretch align-self-md-auto">
        <div class="action-buttons d-flex gap-2 justify-content-end justify-content-md-start w-100 w-md-auto flex-wrap">
          <button
            v-if="isActive || isPaused"
            class="bb-btn-secondary-cta btn-sm text-nowrap"
            @click.stop="handleAction('toggle')"
            :title="isPaused ? 'Resume' : 'Pause'"
          >
            <i :class="isPaused ? 'bi-play' : 'bi-pause'" class="me-1"></i>
            {{ isPaused ? "Resume" : "Pause" }}
          </button>

          <button
            class="bb-btn-primary-cta btn-sm text-nowrap"
            @click.stop="handleAction('run-now')"
            title="Run Now"
          >
            <i class="bi-play-fill me-1"></i>Run Now
          </button>

          <button
            class="bb-btn-danger btn-sm text-nowrap"
            @click.stop="handleAction('delete')"
            title="Delete Schedule"
          >
            <i class="bi-trash me-1"></i>Delete
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
// Theme via Bootstrap CSS custom properties (runtime values) rather than SCSS compile-time
// variables, so the card follows the active theme preset — including dark mode — instead of being
// baked to the light palette at build time.
.schedule-card {
  border: 1px solid var(--bs-border-color);
  border-top: none;
  background: var(--bs-card-bg);
  transition: all 0.2s;
}

// Round the list as a whole — soft outer corners on the first/last card, flat inner divisions —
// so the stacked cards read as one cohesive list consistent with the app's rounded surfaces.
.schedule-card:first-child {
  border-top: 1px solid var(--bs-border-color);
  border-top-left-radius: var(--bs-border-radius);
  border-top-right-radius: var(--bs-border-radius);
}

.schedule-card:last-child {
  border-bottom-left-radius: var(--bs-border-radius);
  border-bottom-right-radius: var(--bs-border-radius);
}

.schedule-card:hover {
  background: var(--bs-tertiary-bg);
  border-color: var(--bs-primary);
}

.schedule-card.selected {
  background: var(--bs-primary-bg-subtle);
  border-color: var(--bs-primary);
}

.selection-area {
  padding-top: 0.25rem;
}

.schedule-main {
  min-width: 0;
  cursor: pointer;
}

.product-tag {
  background: $gray-200;
  color: $gray-700;
  padding: 0.125rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
}

.schedule-timing {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 1rem;
}

.timing-item {
  min-width: 0;
}

.timing-label {
  font-size: 0.75rem;
  letter-spacing: 0.025em;
}

.run-status i {
  font-size: 0.75rem;
}

// Class names match the lowercased ScheduleRunStatus bound in the template (succeeded/failed/running).
.run-status.succeeded {
  color: $success;
}

.run-status.failed {
  color: $danger;
}

// "Running" here means fired-but-outcome-pending — neutral muted colour, no animation.
.run-status.running {
  color: $text-muted;
}
</style>
